// Dashnode AI - Central Manufacturing Skills & Autonomous Agents Definitions

export const AVAILABLE_SKILLS_AGENTS = [
  {
    id: 'costing',
    tag: '@Costing',
    name: 'Costing Agent',
    skill: 'Quote & Unit Economics',
    role: 'Quote & Unit Economics',
    icon: 'Calculator',
    color: '#0C64E8',
    bgColor: '#EFF5FE',
    borderColor: '#C6D6EE',
    badge: 'Available',
    isAvailable: true,
    activeByDefault: true,
    description: 'Calculates raw material volume, machining cycle time, tooling depreciation, volume tier breaks, and manufacturing quotations.'
  },
  {
    id: 'dfm',
    tag: '@DFM',
    name: 'DFM Agent',
    skill: 'Design for Manufacturability',
    role: 'Design for Manufacturability',
    icon: 'Ruler',
    color: '#0C8A74',
    bgColor: '#E4F5EF',
    borderColor: '#BFE7DB',
    badge: 'Available',
    isAvailable: true,
    activeByDefault: true,
    description: 'Validates geometric manufacturability, wall thicknesses, deep pocket radii, undercut access, standard thread depths, and draft angles.'
  },
  {
    id: 'manufacturing',
    tag: '@Manufacturing',
    name: 'Manufacturing Agent',
    skill: 'Process & Toolpaths',
    role: 'Process & Toolpaths',
    icon: 'Cpu',
    color: '#5A6B82',
    bgColor: '#EEF2F7',
    borderColor: '#CBD5E1',
    badge: 'Pipeline',
    isAvailable: false,
    activeByDefault: false,
    description: 'Optimizes CNC machine routing, fixturing setups, G-code strategy, and scrap rates.'
  },
  {
    id: 'design',
    tag: '@Design',
    name: 'Design Agent',
    skill: 'CAD Topology & Weight',
    role: 'CAD Topology & Weight',
    icon: 'PenTool',
    color: '#0C64E8',
    bgColor: '#EFF5FE',
    borderColor: '#C6D6EE',
    badge: 'Pipeline',
    isAvailable: false,
    activeByDefault: false,
    description: 'Suggests parametric modifications, topology optimization, weight shedding, and fastener clearances.'
  },
  {
    id: 'simulation',
    tag: '@Simulation',
    name: 'Simulation Agent',
    skill: 'FEA Stress & Thermal',
    role: 'FEA Stress & Thermal',
    icon: 'Activity',
    color: '#2E6BD6',
    bgColor: '#E8F0FD',
    borderColor: '#C4D8FB',
    badge: 'Pipeline',
    isAvailable: false,
    activeByDefault: false,
    description: 'Evaluates Von Mises stress, Factor of Safety (FoS), fatigue lifecycle, and modal resonances.'
  },
  {
    id: 'quality',
    tag: '@Quality',
    name: 'Quality Agent',
    skill: 'GD&T & Metrology',
    role: 'GD&T & Metrology',
    icon: 'ShieldCheck',
    color: '#D45A7A',
    bgColor: '#FBEAF0',
    borderColor: '#F5CBD7',
    badge: 'Pipeline',
    isAvailable: false,
    activeByDefault: false,
    description: 'Defines inspection plans, CMM point routines, First Article Inspection (FAI) reports, and AS9102 packages.'
  },
  {
    id: 'procurement',
    tag: '@Procurement',
    name: 'Procurement Agent',
    skill: 'Raw Material & RFQs',
    role: 'Raw Material & RFQs',
    icon: 'Truck',
    color: '#E0A50E',
    bgColor: '#FDF4E0',
    borderColor: '#F6DF9F',
    badge: 'Pipeline',
    isAvailable: false,
    activeByDefault: false,
    description: 'Tracks certified billet suppliers, ASME III certs, mill test reports (MTR), lead times, and inventory levels.'
  }
];

export const INITIAL_SKILLS_AGENTS = AVAILABLE_SKILLS_AGENTS;
export const INITIAL_ENGINEERS = AVAILABLE_SKILLS_AGENTS;

export const INITIAL_MEMORY_STATEMENTS = [];

// Reference / Example Engineering Tasks matching dashnode-workspace.html
export const REFERENCE_THREADS = {
  smr: {
    id: 'smr',
    title: 'SMR core wall redesign',
    short: 'SMR core wall redesign',
    tone: 'purple',
    icon: 'rnd',
    time: '2h',
    timestamp: '2h ago',
    timeGroup: 'Today',
    fileName: 'smr_core_wall_v3.step',
    fileSize: 'STEP · 14.2 MB',
    chain: ['rnd', 'design', 'sim', 'dfm', 'proc'],
    intro: 'Six steps across five engineers. Design and Simulation will loop until the wall clears your margins.',
    plan: ['rnd', 'design', 'sim', 'dfm', 'proc'],
    planLoop: 'Simulation loops back to Design until every margin passes.',
    deliver: {
      title: 'Fab-ready package for L&T',
      sub: 'Everything the supplier needs to quote and build the Gen-3 core wall.',
      files: ['2D GA drawing + full GD&T', '3D STEP model', 'Material & weld-spec callouts', 'Supplier RFQ'],
      actions: [
        ['primary', 'send', 'Send RFQ to L&T'],
        ['ghost', 'file', 'Open package']
      ],
      foot: 'This replaced SolidWorks → Ansys → CATIA → drawings → supplier email — run once, in one place.'
    },
    messages: [
      {
        id: 'msg-smr-u1',
        sender: 'user',
        timestamp: '10:14 AM',
        text: 'New SMR core wall for Gen-3 — it runs about 80 °C hotter. Redesign from our current spec, confirm it holds, and give me fab-ready drawings for our suppliers.'
      },
      {
        id: 'msg-smr-a1',
        sender: 'assistant',
        timestamp: '10:15 AM',
        chain: ['rnd', 'design', 'sim', 'dfm', 'proc'],
        intro: 'Six steps across five engineers. Design and Simulation will loop until the wall clears your margins.',
        plan: ['rnd', 'design', 'sim', 'dfm', 'proc'],
        planLoop: 'Simulation loops back to Design until every margin passes.',
        deliver: {
          title: 'Fab-ready package for L&T',
          sub: 'Everything the supplier needs to quote and build the Gen-3 core wall.',
          files: ['2D GA drawing + full GD&T', '3D STEP model', 'Material & weld-spec callouts', 'Supplier RFQ'],
          actions: [
            ['primary', 'send', 'Send RFQ to L&T'],
            ['ghost', 'file', 'Open package']
          ],
          foot: 'This replaced SolidWorks → Ansys → CATIA → drawings → supplier email — run once, in one place.'
        },
        text: `### R&D Engineer · Running
* Pulled the Gen-2 core wall spec and thermal envelope from memory.
* New duty: coolant outlet **340 °C → 420 °C**, pressure unchanged at **15.5 MPa**.
* At 420 °C the Gen-2 316L hot zone drops to a **1.21 thermal-fatigue margin** — under your 1.40 standard.

> 🧠 *From memory · Gen-2 core wall spec · fatigue margin ≥ 1.40*

---

### Design Engineer · Running
* Regenerated the parametric wall from the Gen-2 baseline.
* Hot-zone wall **42 → 48 mm**; added a gradient-relief groove at the transition.
* Moved the hot zone to **Inconel 625** — the same call you made on the Gen-2 barrel.

| Parameter | Specification | Baseline | Change |
|---|---|---|---|
| Hot-zone wall | **48 mm** | 42 mm | +6 mm |
| Hot zone material | **Inconel 625** | SS 316L | Upgraded |
| Relief groove | **R3** | None | Added |
| Total Mass Δ | **+6.4%** | Baseline | Controlled |

> 🧠 *From memory · Gen-2: 316L → Inconel 625 at hot zone*

---

### Simulation Engineer · Running
* Ran structural and thermal on the new geometry with Ansys Mechanical + Fluent.
* Peak stress: **186 MPa** (vs 310 MPa yield)
* Stress margin: **1.67**
* Thermal gradient: **1.9**

> ⚠️ **Stress concentration at the groove fillet (R3) → margin 1.28.** Below your 1.40 standard. Looping back to Design engineer.

---

### Design Engineer · Revision
* Opened the fillet **R3 → R6** and re-ran the check with Simulation.
* Groove margin now **1.52**. Geometry frozen.

---

### DFM Engineer · Running
* Two-piece build: **Inconel 625 hot-zone ring + 316L barrel**, joined at a qualified girth weld.
* All features sit inside your suppliers’ capability.

> ℹ️ The 48 mm wall needs a **forged ring, not plate** — that narrows the vendor set.

---

### Procurement Engineer · Running
* Matched the forged-ring requirement against your qualified fab suppliers.

| Supplier | Capability | Lead Time | Fit |
|---|---|---|---|
| **L&T Heavy Engineering** | Forging + ASME III | 14 wk | ✅ **Recommended** |
| **Walchandnagar Inds.** | Forging | 16 wk | Qualified |
| **Godrej Process Eqpt.** | Plate only | — | ❌ Can’t forge |

> 🧠 *From memory · Gen-2 barrel → L&T · strong ASME III turnaround*`,
        tasks: [
          {
            id: 'task-smr-1',
            tool: 'list_features',
            agent: 'R&D',
            args: { component: 'SMR core wall', envelope: 'Gen-2 baseline' },
            status: 'completed',
            result: 'Retrieved thermal envelope: 340°C → 420°C, 15.5 MPa pressure.'
          },
          {
            id: 'task-smr-2',
            tool: 'run_simulation',
            agent: 'Simulation',
            args: { solver: 'Ansys Mechanical', geometry: 'R3 fillet' },
            status: 'completed',
            result: 'Fillet R3 stress margin 1.28 flagged (< 1.40 threshold). Updated to R6: margin 1.52 passed.'
          },
          {
            id: 'task-smr-3',
            tool: 'run_dfm_analysis',
            agent: 'DFM',
            args: { process: 'Forging ring', weld: 'ASME III girth' },
            status: 'completed',
            result: 'Requires forged ring. L&T Heavy Engineering qualified with 14-week turnaround.'
          }
        ]
      }
    ]
  },

  tradeoff: {
    id: 'tradeoff',
    title: 'Shield-wall trade study',
    short: 'Shield-wall trade study',
    tone: 'indigo',
    icon: 'sim',
    time: '1d',
    timestamp: '1d ago',
    timeGroup: 'Yesterday',
    chain: ['rnd', 'sim', 'design', 'cost'],
    intro: 'Four engineers explore the space — not the first answer that works, the best one across the options.',
    plan: ['rnd', 'sim', 'design', 'cost'],
    deliver: {
      title: 'Recommendation: borated stainless (Option A)',
      sub: 'Gets the full 15% at the lowest mass and a modest cost — and clears the limit that blocked it in 2024.',
      files: ['Three-option trade study', 'Flux & stress results', 'Updated 2024 decision note'],
      actions: [
        ['primary', 'file', 'Open trade study'],
        ['ghost', 'design', 'Take Option A to design']
      ],
      foot: 'Three routes explored and ranked — not the first one that happened to work.'
    },
    messages: [
      {
        id: 'msg-to-u1',
        sender: 'user',
        timestamp: 'Yesterday',
        text: 'We want the shield wall 15% thinner. What does that force?'
      },
      {
        id: 'msg-to-a1',
        sender: 'assistant',
        timestamp: 'Yesterday',
        chain: ['rnd', 'sim', 'design', 'cost'],
        intro: 'Four engineers explore the space — not the first answer that works, the best one across the options.',
        plan: ['rnd', 'sim', 'design', 'cost'],
        deliver: {
          title: 'Recommendation: borated stainless (Option A)',
          sub: 'Gets the full 15% at the lowest mass and a modest cost — and clears the limit that blocked it in 2024.',
          files: ['Three-option trade study', 'Flux & stress results', 'Updated 2024 decision note'],
          actions: [
            ['primary', 'file', 'Open trade study'],
            ['ghost', 'design', 'Take Option A to design']
          ],
          foot: 'Three routes explored and ranked — not the first one that happened to work.'
        },
        text: `### R&D Engineer · Running
* Pulled the shield-wall spec and your own 2024 note on this exact change.

> ⚠️ **You rejected 15% thinner in 2024** — neutron flux leakage went over the limit. Two things have changed since: a qualified borated-steel grade, and the Gen-3 duty cycle.
> 🧠 *From memory · 2024 shield-wall decision*

---

### Simulation Engineer · Running
* At 15% thinner in the current 316L, flux leakage is **1.18× your limit** — the same wall you hit in 2024.
* Thinner only works if the material or the layout changes. Explored three routes.

---

### Design + Costing Engineers · Trade Space Exploration

#### Option A: Borated stainless, 15% thinner (Recommended)
* **Flux leakage**: 0.94× limit *(Compliant)*
* **Mass Δ**: −12% *(Favorable)*
* **Cost Δ**: +9%
* *Now a qualified grade — it was not in 2024. Clears the exact limit that blocked this before.*

#### Option B: Keep 316L, add a tungsten liner
* **Flux leakage**: 0.91× limit
* **Mass Δ**: −4%
* **Cost Δ**: +22%
* *Proven material, but the liner adds significant cost and most of the weight back.*

#### Option C: Thinner 316L + external shield ring
* **Flux leakage**: 0.96× limit
* **Mass Δ**: +6%
* **Cost Δ**: +3%
* *Cheapest route, but adds assembly weight and one more joint to qualify.*`,
        tasks: [
          {
            id: 'task-to-1',
            tool: 'query_material_catalog',
            agent: 'R&D',
            args: { grade: 'Borated stainless', standard: 'ASME BPVC' },
            status: 'completed',
            result: 'Verified qualified grade: borated stainless absorbs thermal neutron flux.'
          }
        ]
      }
    ]
  },

  cost: {
    id: 'cost',
    title: 'Assembly shoot cost',
    short: 'Assembly shoot cost',
    tone: 'blue',
    icon: 'cost',
    time: 'Mon',
    timestamp: 'Mon',
    timeGroup: 'Previous 7 Days',
    chain: ['cost', 'dfm', 'proc'],
    intro: 'Three engineers. Costing builds the BOM, DFM and Procurement decide make versus buy.',
    plan: ['cost', 'dfm', 'proc'],
    deliver: {
      title: 'Costed assembly — make-vs-buy map',
      sub: 'Full control-rod drive assembly, every sub-component priced and sourced.',
      metrics: [
        ['Per unit', '₹1,49,150'],
        ['At 50 units', '₹74.6 L'],
        ['Split', 'Make 2 · Buy 4']
      ],
      files: ['Line-item BOM cost sheet', 'Make-vs-buy rationale', 'Vendor quote pack'],
      actions: [
        ['primary', 'file', 'Open breakdown'],
        ['ghost', 'proc', 'Issue RFQs']
      ],
      foot: 'Every sub-component costed and sourced in one pass — not one RFQ at a time.'
    },
    messages: [
      {
        id: 'msg-cs-u1',
        sender: 'user',
        timestamp: 'Monday',
        text: 'Cost the full control-rod drive assembly at 50 units — every component and sub-assembly, and flag what we should make versus buy.'
      },
      {
        id: 'msg-cs-a1',
        sender: 'assistant',
        timestamp: 'Monday',
        chain: ['cost', 'dfm', 'proc'],
        intro: 'Three engineers. Costing builds the BOM, DFM and Procurement decide make versus buy.',
        plan: ['cost', 'dfm', 'proc'],
        deliver: {
          title: 'Costed assembly — make-vs-buy map',
          sub: 'Full control-rod drive assembly, every sub-component priced and sourced.',
          metrics: [
            ['Per unit', '₹1,49,150'],
            ['At 50 units', '₹74.6 L'],
            ['Split', 'Make 2 · Buy 4']
          ],
          files: ['Line-item BOM cost sheet', 'Make-vs-buy rationale', 'Vendor quote pack'],
          actions: [
            ['primary', 'file', 'Open breakdown'],
            ['ghost', 'proc', 'Issue RFQs']
          ],
          foot: 'Every sub-component costed and sourced in one pass — not one RFQ at a time.'
        },
        text: `### Costing Engineer · Running
* Read the assembly BOM — 6 components across 2 sub-assemblies.
* Costed each at 50 units using your machine-hour and material rates.

| Component | Material | Qty | Unit Cost | Route |
|---|---|---|---|---|
| **Drive housing** | SA-508 Gr.3 | 50 | ₹42,300 | 🏷️ **Buy** |
| **Lead screw** | 17-4 PH | 50 | ₹18,900 | ⚙️ **Make** |
| **Latch mechanism** *(sub-asm)* | SS 316L | 50 | ₹27,600 | ⚙️ **Make** |
| **Position-coil housing** | Inconel 625 | 50 | ₹31,200 | 🏷️ **Buy** |
| **Pressure-boundary tube** | SS 316L | 50 | ₹22,400 | 🏷️ **Buy** |
| **Seals & fasteners** | Standard | 50 | ₹6,750 | 🏷️ **Buy** |

> 🧠 *From memory · Machine-hour & material rates from Kaveri Nuclear profile*

---

### DFM Engineer · Running
* Two parts carry most of the cost risk — checked both against your routing.
* **Drive housing**: deep bore, tight ID — only 3 vendors hold it, cheaper to buy.
* **Lead screw**: thread grinding is already in your shop routing spec — cheaper to make.

---

### Procurement Engineer · Running
* Pulled current-equivalent quotes for buy parts against qualified vendors.

| Buy Part | Best Vendor | Landed Price | Lead Time |
|---|---|---|---|
| **Drive housing** | Godrej Process Eqpt. | ₹41,100 | 11 wk |
| **Coil housing** | MTAR Technologies | ₹30,400 | 13 wk |`,
        tasks: [
          {
            id: 'task-cs-1',
            tool: 'calculate_bom_cost',
            agent: 'Costing',
            args: { quantity: 50, standard_rates: 'Kaveri Nuclear' },
            status: 'completed',
            result: 'Total BOM per unit calculated: ₹1,49,150. Make: 2, Buy: 4.'
          }
        ]
      }
    ]
  }
};

export const INITIAL_CONVERSATIONS = [
  REFERENCE_THREADS.smr,
  REFERENCE_THREADS.tradeoff,
  REFERENCE_THREADS.cost
];
